import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Send, Crown, Shield, Lock, Trash2, Radio, Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { getToken } from "@/lib/tokenStore";
import { toast } from "sonner";

const GLOBAL_ROOM_ID = "pit-stop";

// UI helpers to keep nested ternaries out of JSX
function roomIconFor(room) {
  if (room.kind === "global") return "global";
  if (room.is_current) return "live";
  return "idle";
}

function badgeTierFor(msg) {
  if (msg.is_admin) return "admin";
  if (msg.is_founding_member) return "founding";
  return "member";
}

function badgeTitleFor(msg) {
  if (msg.is_admin) return "BooBoo · Admin";
  if (msg.is_founding_member) return "Founding Member";
  return "Member";
}

function BADGE_CLASSES(tier) {
  if (tier === "admin") return "bg-[#FF1493] text-[#0A0A0A] border-[#FF1493]";
  if (tier === "founding") return "bg-[#FFC107] text-[#0A0A0A] border-[#FFC107]";
  return "bg-[#141414] text-white border-white/20";
}

function wsUrlFor(roomId, token) {
  const base = (api.defaults?.baseURL || "").replace(/\/$/, "");
  const u = new URL(base);
  u.protocol = u.protocol === "https:" ? "wss:" : "ws:";
  u.pathname = `${u.pathname}/chat/ws/${encodeURIComponent(roomId)}`;
  u.search = `?token=${encodeURIComponent(token || "")}`;
  return u.toString();
}

export default function LoungePage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const isAdmin = user?.role === "admin";
  const isVip = !!user?.tour_pass_active || isAdmin;

  const [rooms, setRooms] = useState([]);
  const [activeRoomId, setActiveRoomId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [connected, setConnected] = useState(false);
  const [online, setOnline] = useState(0);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const wsRef = useRef(null);
  const listRef = useRef(null);
  const pingTimerRef = useRef(null);

  // Fetch rooms
  useEffect(() => {
    if (!user || !isVip) return;
    api
      .get("/chat/rooms")
      .then((r) => {
        const list = r.data || [];
        setRooms(list);
        // Auto-open the current stop's room if we haven't picked one yet.
        // Backend returns current-stop first, so `list[0]` is either
        // the current city or the Pit Stop when there's no current stop.
        setActiveRoomId((prev) => {
          if (prev) return prev;
          const current = list.find((room) => room.is_current);
          return current?.id || list[0]?.id || GLOBAL_ROOM_ID;
        });
      })
      .catch((err) => {
        console.warn("lounge: failed to load rooms", err?.response?.status || err?.message);
      });
  }, [user, isVip]);

  // Load history when room changes
  useEffect(() => {
    if (!isVip || !activeRoomId) return;
    let cancelled = false;
    setLoadingHistory(true);
    api
      .get(`/chat/rooms/${activeRoomId}/messages`, { params: { limit: 80 } })
      .then((r) => {
        if (!cancelled) setMessages(r.data || []);
      })
      .catch(() => {
        if (!cancelled) setMessages([]);
      })
      .finally(() => !cancelled && setLoadingHistory(false));
    return () => {
      cancelled = true;
    };
  }, [activeRoomId, isVip]);

  // WebSocket
  useEffect(() => {
    if (!isVip || !activeRoomId) return;
    const token = getToken();
    if (!token) return;
    let cancelled = false;
    const ws = new WebSocket(wsUrlFor(activeRoomId, token));
    wsRef.current = ws;

    ws.onopen = () => {
      if (cancelled) return;
      setConnected(true);
      pingTimerRef.current = setInterval(() => {
        if (ws.readyState === 1) ws.send(JSON.stringify({ type: "ping" }));
      }, 25000);
    };
    ws.onmessage = (e) => {
      if (cancelled) return;
      try {
        const payload = JSON.parse(e.data);
        if (payload.type === "message") {
          setMessages((m) => [...m, payload.data]);
        } else if (payload.type === "delete") {
          setMessages((m) => m.filter((x) => x.id !== payload.data.id));
        } else if (payload.type === "presence") {
          setOnline(payload.data.online || 0);
        }
      } catch (err) {
        console.warn("lounge: bad ws payload", err);
      }
    };
    ws.onclose = () => {
      setConnected(false);
      if (pingTimerRef.current) clearInterval(pingTimerRef.current);
    };
    ws.onerror = () => {
      setConnected(false);
    };

    return () => {
      cancelled = true;
      if (pingTimerRef.current) clearInterval(pingTimerRef.current);
      try {
        ws.close();
      } catch (err) {
        console.warn("lounge: ws close error (already closed?)", err);
      }
    };
  }, [activeRoomId, isVip]);

  // Autoscroll
  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages, loadingHistory]);

  const send = (e) => {
    e?.preventDefault?.();
    const text = input.trim();
    if (!text) return;
    const ws = wsRef.current;
    if (ws && ws.readyState === 1) {
      ws.send(JSON.stringify({ type: "message", message: text }));
      setInput("");
    } else {
      // REST fallback
      api
        .post(`/chat/rooms/${activeRoomId}/messages`, { message: text })
        .then(() => setInput(""))
        .catch((err) => toast(err?.response?.data?.detail || "Send failed."));
    }
  };

  const adminDelete = async (id) => {
    if (!isAdmin) return;
    if (!window.confirm("Nuke this message?")) return;
    try {
      await api.delete(`/chat/messages/${id}`);
    } catch (e) {
      toast("Couldn't delete.");
    }
  };

  // ---- VIP gate ----
  if (!user) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-6">
        <div className="max-w-md text-center" data-testid="lounge-gated-anon">
          <Lock size={32} className="text-[#FFC107] mx-auto" />
          <h1 className="font-display text-4xl uppercase mt-4 drip-shadow">Locked.</h1>
          <p className="text-white/70 mt-3">
            The lounge is for fans on the bus. Sign in or grab a Tour Pass to get in.
          </p>
          <div className="flex gap-3 justify-center mt-6">
            <Link to="/login" className="btn-ghost" data-testid="lounge-login">Sign in</Link>
            <Link to="/tour-pass" className="btn-blaze" data-testid="lounge-buy-pass">Get Tour Pass</Link>
          </div>
        </div>
      </div>
    );
  }
  if (!isVip) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-6">
        <div className="max-w-md text-center" data-testid="lounge-gated-non-vip">
          <Crown size={32} className="text-[#FFC107] mx-auto" />
          <h1 className="font-display text-4xl uppercase mt-4 drip-shadow">VIP only.</h1>
          <p className="text-white/70 mt-3">
            The lounge is a Tour Pass perk. $9/30 days. Drop in, drop out.
          </p>
          <Link to="/tour-pass" className="btn-blaze mt-6 inline-flex" data-testid="lounge-upgrade">
            Activate Tour Pass
          </Link>
        </div>
      </div>
    );
  }

  const activeRoom = rooms.find((r) => r.id === activeRoomId);

  return (
    <div className="min-h-[80vh] grid grid-cols-1 md:grid-cols-12 max-w-7xl mx-auto px-4 md:px-6 py-10 gap-6" data-testid="lounge-page">
      {/* Sidebar — rooms */}
      <aside className="md:col-span-3">
        <div className="overline">Rooms</div>
        <h1 className="font-display text-3xl uppercase mt-2 leading-none drip-shadow">The Lounge</h1>
        <p className="text-white/60 text-xs mt-2 uppercase tracking-[0.18em] font-mono-tactical">
          VIP · Tour Pass only
        </p>
        <div className="mt-5 space-y-2 max-h-[60vh] overflow-y-auto pr-1">
          {rooms.map((r) => {
            const active = r.id === activeRoomId;
            return (
              <button
                key={r.id}
                onClick={() => setActiveRoomId(r.id)}
                className={`w-full text-left px-3 py-2 border transition-colors ${
                  active
                    ? "bg-[#FF1493] text-[#0A0A0A] border-[#FF1493]"
                    : "bg-[#141414] text-white border-white/10 hover:border-[#FF1493]/60"
                }`}
                data-testid={`lounge-room-${r.id}`}
              >
                <div className="flex items-center gap-2">
                  {(() => {
                    const icon = roomIconFor(r);
                    if (icon === "global") return <Radio size={14} />;
                    if (icon === "live") return <span className="w-2 h-2 rounded-full bg-[#FF1E1E] inline-block animate-pulse" />;
                    return <span className="w-2 h-2 rounded-full bg-white/30 inline-block" />;
                  })()}
                  <span className="font-display text-sm uppercase tracking-wider truncate flex-1">
                    {r.label}
                  </span>
                  {r.online > 0 && (
                    <span className={`text-[10px] font-mono-tactical ${active ? "text-[#0A0A0A]" : "text-white/50"}`}>
                      {r.online}
                    </span>
                  )}
                </div>
                {r.subtitle && (
                  <div className={`text-[10px] mt-1 font-mono-tactical uppercase tracking-[0.15em] truncate ${active ? "text-[#0A0A0A]/70" : "text-white/40"}`}>
                    {r.subtitle}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </aside>

      {/* Main chat */}
      <section className="md:col-span-9 flex flex-col border border-white/10 bg-[#0A0A0A]" data-testid="lounge-room">
        {/* City banner — only for city rooms with an image */}
        {activeRoom?.kind === "city" && activeRoom?.image_url && (
          <div
            className="relative h-32 md:h-40 border-b border-white/10 bg-cover bg-center"
            style={{ backgroundImage: `url(${activeRoom.image_url})` }}
            data-testid="lounge-room-banner"
          >
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/40 to-transparent" />
            {activeRoom.is_current && (
              <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 bg-[#FF1E1E] text-white px-2 py-1 text-[10px] font-display tracking-[0.2em] uppercase">
                <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> BooBoo is here
              </div>
            )}
          </div>
        )}

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <div className="min-w-0">
            <div className="overline">{activeRoom?.kind === "global" ? "Global lounge" : "City room"}</div>
            <h2 className="font-display text-2xl uppercase mt-1 leading-none truncate" data-testid="lounge-room-title">
              {activeRoom?.label || "Loading..."}
            </h2>
            {activeRoom?.welcome && (
              <p className="mt-2 text-white/60 text-xs max-w-md" data-testid="lounge-room-welcome">
                {activeRoom.welcome}
              </p>
            )}
          </div>
          <div className="text-right shrink-0">
            <div className="text-[10px] font-mono-tactical text-white/40 uppercase tracking-[0.2em]">
              {connected ? "live" : "reconnecting..."}
            </div>
            <div className="text-xs text-white/70 mt-1" data-testid="lounge-online">
              <span className="text-[#FFC107] font-display text-lg leading-none">{online}</span> online
            </div>
          </div>
        </div>

        {/* Pinned drop card */}
        {activeRoom?.active_drop && (
          <Link
            to="/shop"
            className="flex items-center gap-4 mx-5 mt-4 p-3 border border-[#FFC107]/40 bg-[#FFC107]/[0.06] hover:border-[#FFC107] transition-colors"
            data-testid="lounge-pinned-drop"
          >
            <div className="w-14 h-14 shrink-0 bg-cover bg-center border border-white/10"
                 style={{ backgroundImage: `url(${activeRoom.active_drop.image_url})` }} />
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-mono-tactical tracking-[0.22em] uppercase text-[#FFC107]">
                Live drop · pinned
              </div>
              <div className="font-display text-base uppercase truncate">{activeRoom.active_drop.name}</div>
              <div className="text-white/60 text-xs mt-0.5">
                ${Number(activeRoom.active_drop.price || 0).toFixed(2)}
                {activeRoom.subtitle && (
                  <span className="text-white/40 ml-2">· {activeRoom.subtitle}</span>
                )}
              </div>
            </div>
            <span className="text-[#FFC107] font-display text-xs tracking-[0.2em] shrink-0">COP IT →</span>
          </Link>
        )}

        {/* Messages */}
        <div
          ref={listRef}
          className="flex-1 overflow-y-auto px-5 py-5 space-y-3 min-h-[50vh] max-h-[60vh]"
          data-testid="lounge-messages"
        >
          {loadingHistory && (
            <div className="text-center text-white/50 text-xs"><Loader2 className="inline animate-spin mr-2" size={14} />loading history...</div>
          )}
          {!loadingHistory && messages.length === 0 && (
            <div className="text-center text-white/40 text-xs font-mono-tactical uppercase tracking-[0.2em] mt-12">
              No messages yet. Break the silence.
            </div>
          )}
          {messages.map((m) => (
            <MessageRow key={m.id} msg={m} isAdmin={isAdmin} onDelete={adminDelete} />
          ))}
        </div>

        {/* Composer */}
        <form onSubmit={send} className="border-t border-white/10 p-3 flex gap-2" data-testid="lounge-composer">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={connected ? "Say something fire..." : "Reconnecting..."}
            maxLength={600}
            className="tac-input flex-1"
            data-testid="lounge-input"
          />
          <button
            type="submit"
            disabled={!input.trim()}
            className="btn-blaze"
            data-testid="lounge-send"
          >
            <Send size={14} /> Send
          </button>
        </form>
      </section>
    </div>
  );
}

function MessageRow({ msg, isAdmin, onDelete }) {
  const num = msg.member_number ? String(msg.member_number).padStart(3, "0") : null;
  const tier = badgeTierFor(msg);
  const badgeIcon = (() => {
    if (msg.is_admin) return <Shield size={16} />;
    if (num) return `#${num}`;
    return "•";
  })();
  return (
    <div className="flex gap-3 group" data-testid={`lounge-msg-${msg.id}`}>
      <div
        className={`w-10 h-10 shrink-0 flex items-center justify-center font-display text-[11px] leading-none border ${BADGE_CLASSES(tier)}`}
        title={badgeTitleFor(msg)}
      >
        {badgeIcon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="font-display text-sm uppercase tracking-wider text-white">
            {msg.name}
          </span>
          {msg.is_admin && <span className="text-[9px] text-[#FF1493] font-mono-tactical uppercase tracking-[0.2em]">Creator</span>}
          {!msg.is_admin && msg.is_founding_member && (
            <span className="text-[9px] text-[#FFC107] font-mono-tactical uppercase tracking-[0.2em]">Founding · #{num}</span>
          )}
          <span className="text-[10px] text-white/30 font-mono-tactical">
            {new Date(msg.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
          </span>
          {isAdmin && (
            <button
              onClick={() => onDelete(msg.id)}
              className="opacity-0 group-hover:opacity-100 text-white/30 hover:text-[#FF1E1E] transition-opacity"
              data-testid={`lounge-msg-delete-${msg.id}`}
              title="Delete (admin)"
            >
              <Trash2 size={12} />
            </button>
          )}
        </div>
        <div className="text-white/85 text-sm mt-1 break-words whitespace-pre-wrap">
          {msg.message}
        </div>
      </div>
    </div>
  );
}
